import React, { useState, useRef } from 'react';
import { X, Mic, Send, Eye, EyeOff, Trash2, CheckCircle2, Loader2, Keyboard, Play, Pause, Maximize2, Sparkles } from 'lucide-react';
import { MindMapNode, Language, ReviewSession } from '../types';
import posthog from 'posthog-js';
import { ImageGalleryModal } from './ImageGalleryModal';
import { useHistory } from '../hooks/useHistory';

interface ReviewSessionModalProps {
    node: MindMapNode;
    onClose: () => void;
    onSaveSession: (session: ReviewSession) => void;
    onDeleteSession?: (sessionId: string) => void;
    language: Language;
}

export const ReviewSessionModal: React.FC<ReviewSessionModalProps> = ({
    node,
    onClose,
    onSaveSession,
    onDeleteSession,
    language
}) => {
    // Recording Mode State
    // Default to Real-time on Desktop (>=768px), Legacy on Mobile
    const isMobile = window.innerWidth < 768; // Simple initial check
    const [useRealTimeDictation, setUseRealTimeDictation] = useState(!isMobile);

    // Audio Recording State (Legacy Mode)
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [hasTranscriptionError, setHasTranscriptionError] = useState(false);

    // Real-time Speech Recognition State
    const [isListening, setIsListening] = useState(false);
    
    // Persistent buffers for real-time dictation
    const finalTranscriptRef = useRef('');
    const interimTranscriptRef = useRef('');

    // ✅ Replaced useState with useHistory for robust Undo/Redo protection
    const {
        state: textInput,
        setState: setTextInput,
        undo,
        redo,
        canUndo,
        canRedo
    } = useHistory('');

    // Handle Undo/Redo Keyboard Shortcuts
    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Undo: Ctrl+Z / Cmd+Z (without Shift)
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            if (canUndo) undo();
            return;
        }

        // Redo: Ctrl+Shift+Z / Cmd+Shift+Z
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            if (canRedo) redo();
            return;
        }
    };

    const [isPeeking, setIsPeeking] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isRefining, setIsRefining] = useState(false); // New state for text cleanup
    const [activeTab, setActiveTab] = useState<'current' | 'history'>('current');
    const [imageGalleryOpen, setImageGalleryOpen] = useState(false);
    const [initialGalleryIndex, setInitialGalleryIndex] = useState(0);
    const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
        const saved = localStorage.getItem('reviewModalLeftPanelWidth');
        return saved ? parseInt(saved) : 45; // Default 45% for left panel
    });

    const allImages = React.useMemo(() => {
        const images: { src: string; alt: string }[] = [];
        if (node.imageBase64) images.push({ src: node.imageBase64, alt: 'Main Image' });
        if (node.drawingBase64) images.push({ src: node.drawingBase64, alt: 'Drawing' });
        if (node.attachments) {
            node.attachments.forEach(att => {
                if (att.type === 'image' && att.content) {
                    images.push({ src: att.content, alt: att.name || 'Attachment' });
                }
            });
        }
        return images;
    }, [node]);
    const [reviewResult, setReviewResult] = useState<{
        score: number;
        feedback: string;
        analysisPoints?: { quote: string; verdict: 'correct' | 'incorrect' | 'partial'; explanation: string }[];
        followUpQuestions?: string[];
    } | null>(null);

    // ... (rest of the file)



    // Audio Recording refs
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const savedAudioBlobRef = useRef<Blob | null>(null); // Backup audio for retry
    const recognitionRef = useRef<any>(null);
    const baseContentRef = useRef<string>(''); // Base content before real-time dictation
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Transcribe audio blob (extracted for retry functionality)
    const transcribeAudioBlob = async (audioBlob: Blob) => {
        setIsTranscribing(true);
        setHasTranscriptionError(false);

        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        reader.onloadend = async () => {
            const base64String = reader.result as string;
            const cleanBase64 = base64String.split(',')[1];
            try {
                const { transcribeAndRefineAudio } = await import('../services/geminiService');
                const refinedText = await transcribeAndRefineAudio(cleanBase64, language);
                if (refinedText) {
                    const newContent = textInput ? `${textInput}\\n\\n${refinedText}` : refinedText;
                    setTextInput(newContent);
                    // Success: clear saved audio
                    savedAudioBlobRef.current = null;
                    setHasTranscriptionError(false);
                }
            } catch (err) {
                console.error("Transcription failed", err);
                setHasTranscriptionError(true);
                // Keep the audio blob for retry
            } finally {
                setIsTranscribing(false);
            }
        };
    };

    // Retry transcription with saved audio
    const handleRetryTranscription = () => {
        if (savedAudioBlobRef.current) {
            transcribeAudioBlob(savedAudioBlobRef.current);
        }
    };

    // Legacy Audio Recording (Upload to Gemini)
    const handleLegacyRecording = async () => {
        if (isRecording) {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
            setIsRecording(false);
        } else {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                const mediaRecorder = new MediaRecorder(stream);
                mediaRecorderRef.current = mediaRecorder;
                audioChunksRef.current = [];

                mediaRecorder.ondataavailable = (event) => {
                    if (event.data.size > 0) audioChunksRef.current.push(event.data);
                };

                mediaRecorder.onstop = async () => {
                    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                    // BACKUP: Save audio blob for potential retry
                    savedAudioBlobRef.current = audioBlob;
                    // Attempt transcription
                    await transcribeAudioBlob(audioBlob);
                    stream.getTracks().forEach(track => track.stop());
                };

                mediaRecorder.start();
                setIsRecording(true);
            } catch (err) {
                console.error("Mic error", err);
                alert('Cannot access microphone.');
            }
        }
    };

    // Real-time Dictation (Web Speech API)
    const handleRealTimeDictation = () => {
        if (isListening) {
            // Stop listening
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
            // Logic handled in onend
        } else {
            // Start listening
            try {
                // @ts-ignore - Web Speech API may not have full TypeScript support
                const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

                if (!SpeechRecognition) {
                    alert(language === 'vietnamese'
                        ? 'Trình duyệt không hỗ trợ chuyển giọng nói thành văn bản. Vui lòng dùng Chrome hoặc Edge.'
                        : 'Speech recognition not supported. Please use Chrome or Edge.');
                    return;
                }

                const recognition = new SpeechRecognition();
                recognition.continuous = true;
                recognition.interimResults = true;
                recognition.lang = language === 'vietnamese' ? 'vi-VN' : 'en-US';

                // Reset refs
                finalTranscriptRef.current = '';
                interimTranscriptRef.current = '';

                // Save base content before starting
                const baseContent = textInput || '';

                recognition.onresult = (event: any) => {
                    let newInterim = '';

                    for (let i = event.resultIndex; i < event.results.length; i++) {
                        const transcript = event.results[i][0].transcript;
                        if (event.results[i].isFinal) {
                            finalTranscriptRef.current += transcript + ' ';
                        } else {
                            newInterim += transcript;
                        }
                    }
                    
                    interimTranscriptRef.current = newInterim;

                    // REAL-TIME: Update UI immediately with both final + interim text
                    const displayText = baseContent + (baseContent && !baseContent.endsWith('\n') ? ' ' : '') + finalTranscriptRef.current + interimTranscriptRef.current;
                    setTextInput(displayText);
                };

                recognition.onerror = (event: any) => {
                    console.error('Speech recognition error:', event.error);
                    if (event.error !== 'no-speech') {
                        setIsListening(false);
                         if (event.error !== 'aborted') {
                            const errorMessages: Record<string, { vi: string; en: string }> = {
                                'not-allowed': {
                                    vi: 'Vui lòng cấp quyền sử dụng microphone.',
                                    en: 'Please grant microphone permission.'
                                },
                                'network': {
                                    vi: 'Lỗi mạng. Vui lòng kiểm tra kết nối.',
                                    en: 'Network error. Please check your connection.'
                                }
                            };
                            const message = errorMessages[event.error]?.[language === 'vietnamese' ? 'vi' : 'en']
                                || (language === 'vietnamese' ? 'Lỗi nhận diện giọng nói.' : 'Speech recognition error.');
                            alert(message);
                         }
                    }
                };

                recognition.onend = () => {
                    setIsListening(false);
                    
                    // CRITICAL FIX: Save EVERYTHING when stopped
                    const allText = finalTranscriptRef.current + interimTranscriptRef.current;
                    if (allText.trim()) {
                         const finalText = baseContent + (baseContent && !baseContent.endsWith('\n') ? ' ' : '') + allText;
                         setTextInput(finalText);
                    }
                    
                    // Cleanup
                    finalTranscriptRef.current = '';
                    interimTranscriptRef.current = '';
                };

                recognitionRef.current = recognition;
                recognition.start();
                setIsListening(true);

            } catch (err) {
                console.error('Speech recognition initialization error:', err);
                alert(language === 'vietnamese'
                    ? 'Không thể khởi động chuyển giọng nói thành văn bản.'
                    : 'Failed to start speech recognition.');
            }
        }
    };

    // Main recording handler (routes to appropriate mode)
    const handleVoiceRecording = () => {
        if (useRealTimeDictation) {
            handleRealTimeDictation();
        } else {
            handleLegacyRecording();
        }
    };

    // Clean Up / Refine Text Handler
    const handleRefineText = async () => {
        if (isRefining || !textInput.trim()) return;

        setIsRefining(true);
        try {
            const { refineText } = await import('../services/geminiService');
            const refined = await refineText(textInput, language);
            setTextInput(refined);
        } catch (error) {
            console.error("Refining failed", error);
            alert(language === 'vietnamese' ? 'Không thể làm sạch văn bản.' : 'Failed to refine text.');
        } finally {
            setIsRefining(false);
        }
    };

    const handleClose = () => {
        // Warn user if there's unsent audio from failed transcription
        if (hasTranscriptionError && savedAudioBlobRef.current) {
            const confirmMessage = language === 'vietnamese'
                ? 'Bạn có đoạn ghi âm chưa gửi lên AI. Đóng modal sẽ mất dữ liệu này. Bạn có chắc muốn đóng?'
                : 'You have an unsent audio recording. Closing will discard this data. Are you sure?';

            if (!window.confirm(confirmMessage)) {
                return; // User chose to stay
            }
            // User confirmed: clear the saved audio and close
            savedAudioBlobRef.current = null;
            setHasTranscriptionError(false);
        }
        onClose();
    };

    // Submit explanation for AI review
    const handleSubmit = async () => {
        if (!textInput.trim()) return;
        setIsProcessing(true);
        setReviewResult(null); // Clear previous result

        try {
            const { reviewExplanation } = await import('../services/geminiService');
            const result = await reviewExplanation(
                textInput,
                node.content || '',
                node.imageBase64 || null,
                node.imageMimeType || null,
                language
            );

            posthog.capture('memory_gym_reviewed', { score: result.score });
            setReviewResult({
                score: result.score,
                feedback: result.feedback,
                analysisPoints: result.analysisPoints,
                followUpQuestions: result.followUpQuestions
            });

            // Save session via callback
            onSaveSession({
                userExplanation: result.userExplanation,
                score: result.score,
                feedback: result.feedback,
                analysisPoints: result.analysisPoints,
                followUpQuestions: result.followUpQuestions,
                timestamp: result.timestamp
            });

        } catch (err) {
            console.error('Review failed:', err);
            alert('Failed to review explanation. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    // Handle resize drag
    const handleResize = (e: React.MouseEvent) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidth = leftPanelWidth;
        const modalElement = e.currentTarget.closest('.review-modal-container');
        if (!modalElement) return;
        const modalWidth = modalElement.getBoundingClientRect().width;
        let currentWidth = startWidth;

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const delta = moveEvent.clientX - startX;
            const deltaPercent = (delta / modalWidth) * 100;
            currentWidth = Math.min(Math.max(startWidth + deltaPercent, 20), 70); // Min 20%, max 70%
            setLeftPanelWidth(currentWidth);
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            localStorage.setItem('reviewModalLeftPanelWidth', currentWidth.toString());
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <div className="fixed inset-0 z-[100] flex md:items-center items-end justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="w-full md:max-w-7xl h-[100dvh] md:h-[90vh] bg-white dark:bg-slate-900 md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col overflow-hidden border-t md:border border-gray-200 dark:border-slate-700">

                {/* Header */}
                <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-900 shrink-0">
                    <div className="flex-1">
                        <h2 className="text-lg md:text-xl font-black text-gray-800 dark:text-gray-100 flex items-center gap-2">
                            🏋️ Memory Gym
                        </h2>
                        <p className="text-xs md:text-sm text-gray-400 font-medium">Topic: <span className="text-indigo-600 dark:text-indigo-400">{node.title}</span></p>
                    </div>

                    {/* Peek button - Mobile only */}
                    <button
                        onClick={() => setIsPeeking(!isPeeking)}
                        className="md:hidden p-2 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 rounded-full transition-colors text-yellow-600 dark:text-yellow-500 mr-2"
                        title={isPeeking ? "Hide Note" : "Peek Note"}
                    >
                        {isPeeking ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>

                    <button onClick={handleClose} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors text-gray-500">
                        <X size={20} className="md:hidden" />
                        <X size={24} className="hidden md:block" />
                    </button>
                </div>

                <div className="flex-1 flex flex-col md:flex-row overflow-hidden review-modal-container">
                    {/* Main Stage (Left) - On mobile: Top part (Image + Peek), scalable height */}
                    <div
                        className="hidden md:flex shrink-0 md:shrink-1 max-h-[35vh] md:max-h-none md:h-auto flex-col p-2 md:p-6 bg-gray-50 dark:bg-slate-950/50 relative overflow-y-auto border-b md:border-b-0 w-full"
                        style={{ width: window.innerWidth >= 768 ? `${leftPanelWidth}%` : '100%' }}
                    >

                        {/* Visual Context (Image/Diagram) */}
                        <div className={`flex-shrink-0 flex items-center mb-2 md:mb-6 ${allImages.length > 0 ? '' : 'min-h-[100px] md:min-h-[300px] justify-center'} border-2 border-dashed border-gray-200 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-900 p-2 md:p-4 overflow-hidden relative group`}>
                            {allImages.length > 0 ? (
                                <div className="flex w-full gap-4 overflow-x-auto pb-2 snap-x items-start scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-slate-700">
                                    {allImages.map((img, idx) => (
                                        <div
                                            key={idx}
                                            className="shrink-0 w-full snap-center relative cursor-zoom-in"
                                            onClick={() => { setInitialGalleryIndex(idx); setImageGalleryOpen(true); }}
                                        >
                                            <img
                                                src={img.src}
                                                alt={img.alt}
                                                className="w-full rounded-lg shadow-sm hover:scale-[1.02] transition-transform object-contain bg-white dark:bg-slate-900"
                                            />
                                            {allImages.length > 1 && (
                                                <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
                                                    {idx + 1}/{allImages.length}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center text-gray-400 w-full">
                                    <div className="bg-gray-100 dark:bg-slate-800 p-4 rounded-full inline-block mb-3">
                                        <EyeOff size={32} />
                                    </div>
                                    <p className="text-sm font-medium">No visual context available</p>
                                    <p className="text-xs mt-1">Add images or drawings to this node to see them here.</p>
                                </div>
                            )}
                        </div>

                        {/* Hidden Text Area (Peekable) */}
                        <div className={`transition-all duration-300 relative rounded-xl border border-yellow-200 dark:border-yellow-900/30 bg-yellow-50 dark:bg-yellow-900/10 flex flex-col ${isPeeking ? 'flex-1 min-h-[120px]' : 'h-9 md:h-12 overflow-hidden cursor-pointer hover:bg-yellow-100 shrink-0'}`}>
                            <div
                                className="flex items-center justify-between px-2 md:px-4 py-1.5 md:py-3 cursor-pointer shrink-0"
                                onClick={() => setIsPeeking(!isPeeking)}
                            >
                                <span className="text-[10px] md:text-xs font-bold uppercase tracking-wider text-yellow-600 dark:text-yellow-500 flex items-center gap-1.5">
                                    {isPeeking ? <EyeOff size={12} className="md:hidden" /> : <Eye size={12} className="md:hidden" />}
                                    {isPeeking ? <EyeOff size={14} className="hidden md:block" /> : <Eye size={14} className="hidden md:block" />}
                                    {isPeeking ? "Hide Original Note" : "Peek Original Note"}
                                </span>
                            </div>
                            {isPeeking && (
                                <div className="px-2 md:px-4 pb-2 md:pb-4 prose prose-sm dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 overflow-y-auto custom-scrollbar whitespace-pre-wrap text-xs md:text-base">
                                    {node.content || "(No content)"}
                                </div>
                            )}
                        </div>

                    </div>

                    {/* Resize Handle - Desktop only */}
                    <div
                        onMouseDown={handleResize}
                        className="hidden md:block w-1 bg-gray-200 dark:bg-slate-700 hover:bg-indigo-500 dark:hover:bg-indigo-600 cursor-col-resize transition-colors shrink-0 group relative"
                    >
                        <div className="absolute inset-y-0 -left-1 -right-1" /> {/* Wider hit area */}
                    </div>

                    {/* Interaction Zone (Right) - Full width on mobile */}
                    <div className="flex-1 border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col shrink-0 overflow-hidden">

                        {/* History Tabs */}
                        <div className="h-10 md:h-12 border-b border-gray-100 dark:border-slate-800 flex items-center px-2 md:px-4 gap-2 md:gap-4 text-xs md:text-sm font-bold text-gray-400 shrink-0">
                            <button
                                onClick={() => setActiveTab('current')}
                                className={`h-full flex items-center px-1.5 md:px-2 border-b-2 transition-colors ${activeTab === 'current' ? 'text-indigo-600 border-indigo-600' : 'border-transparent hover:text-gray-600'}`}
                            >
                                Current Session
                            </button>
                            <button
                                onClick={() => setActiveTab('history')}
                                className={`h-full flex items-center px-1.5 md:px-2 border-b-2 transition-colors ${activeTab === 'history' ? 'text-indigo-600 border-indigo-600' : 'border-transparent hover:text-gray-600'}`}
                            >
                                History ({node.reviewSessions?.length || 0})
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div className="flex-1 overflow-hidden relative">
                            {activeTab === 'current' ? (
                                <div className="absolute inset-0 flex flex-col p-3 md:p-6">
                                    {/* Mobile Image Preview (Compact & Always Visible) */}
                                    {allImages.length > 0 && (
                                        <div className="md:hidden mb-3 shrink-0 relative group flex gap-2 overflow-x-auto pb-2 snap-x scrollbar-hide">
                                            {allImages.map((img, idx) => (
                                                <div
                                                    key={idx}
                                                    className="h-32 w-48 shrink-0 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 flex items-center justify-center overflow-hidden cursor-zoom-in snap-center relative"
                                                    onClick={() => { setInitialGalleryIndex(idx); setImageGalleryOpen(true); }}
                                                >
                                                    <img
                                                        src={img.src}
                                                        alt={img.alt}
                                                        className="h-full w-full object-contain"
                                                    />
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setInitialGalleryIndex(idx); setImageGalleryOpen(true); }}
                                                        className="absolute bottom-1 right-1 bg-black/50 text-white p-1 rounded-full backdrop-blur-sm"
                                                    >
                                                        <Maximize2 size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Explanation Textarea (Always visible) */}
                                    <textarea
                                        ref={textareaRef}
                                        className="w-full flex-1 resize-none bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg md:rounded-xl p-3 md:p-4 focus:ring-2 ring-indigo-500 outline-none text-sm md:text-base leading-relaxed"
                                        placeholder="Explain what you remember about this topic..."
                                        value={textInput}
                                        onChange={(e) => setTextInput(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        disabled={!!reviewResult}
                                    />

                                    {/* Review Result Display */}
                                    {reviewResult && (
                                        <div className="flex-1 overflow-y-auto -mx-6 px-6">
                                            <div className={`mt-4 p-4 rounded-xl border-2 mb-4 ${reviewResult.score >= 80 ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' :
                                                reviewResult.score >= 60 ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' :
                                                    'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                                                }`}>
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Your Score</span>
                                                    <span className={`text-3xl font-black ${reviewResult.score >= 80 ? 'text-green-600' :
                                                        reviewResult.score >= 60 ? 'text-yellow-600' :
                                                            'text-red-600'
                                                        }`}>{reviewResult.score}%</span>
                                                </div>
                                                <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-3">
                                                    <div
                                                        className={`h-full transition-all duration-1000 ${reviewResult.score >= 80 ? 'bg-green-500' :
                                                            reviewResult.score >= 60 ? 'bg-yellow-500' :
                                                                'bg-red-500'
                                                            }`}
                                                        style={{ width: `${reviewResult.score}%` }}
                                                    />
                                                </div>
                                                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-medium">{reviewResult.feedback}</p>
                                            </div>

                                            {/* Analysis Points */}
                                            {reviewResult.analysisPoints && reviewResult.analysisPoints.length > 0 && (
                                                <div className="space-y-3 mb-4">
                                                    <h3 className="text-xs font-bold uppercase text-gray-400 tracking-wider">Detailed Analysis</h3>
                                                    {reviewResult.analysisPoints.map((point, idx) => (
                                                        <div key={idx} className={`p-3 rounded-lg border text-sm ${point.verdict === 'correct' ? 'bg-green-50/50 border-green-100 dark:border-green-900/30' :
                                                            point.verdict === 'incorrect' ? 'bg-red-50/50 border-red-100 dark:border-red-900/30' :
                                                                'bg-yellow-50/50 border-yellow-100 dark:border-yellow-900/30'
                                                            }`}>
                                                            <div className="flex gap-2">
                                                                <span className="shrink-0 mt-0.5">
                                                                    {point.verdict === 'correct' && '✅'}
                                                                    {point.verdict === 'incorrect' && '❌'}
                                                                    {point.verdict === 'partial' && '⚠️'}
                                                                </span>
                                                                <div>
                                                                    <p className="italic text-gray-500 mb-1">"{point.quote}"</p>
                                                                    <p className="text-gray-700 dark:text-gray-300">{point.explanation}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Follow-up Questions */}
                                            {reviewResult.followUpQuestions && reviewResult.followUpQuestions.length > 0 && (
                                                <div className="space-y-3 mb-4">
                                                    <h3 className="text-xs font-bold uppercase text-indigo-500 tracking-wider">🧐 Follow-up Questions</h3>
                                                    <div className="bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-800 rounded-xl p-4">
                                                        <ul className="list-disc list-outside ml-4 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                                                            {reviewResult.followUpQuestions.map((q, idx) => (
                                                                <li key={idx} className="pl-1">{q}</li>
                                                            ))}
                                                        </ul>
                                                        <p className="mt-3 text-xs text-indigo-400 italic">
                                                            (Tip: Answer these in your next review or below to improve your score!)
                                                        </p>
                                                    </div>
                                                </div>
                                            )}


                                            {/* Action Buttons */}
                                            <div className="flex gap-2">
                                                {/* Answer Follow-up Button (Primary) */}
                                                {reviewResult.followUpQuestions && reviewResult.followUpQuestions.length > 0 && (
                                                    <button
                                                        onClick={() => {
                                                            // Build formatted question list
                                                            const questionsList = reviewResult.followUpQuestions!
                                                                .map((q, idx) => `❓ Câu ${idx + 1}: ${q}`)
                                                                .join('\n');

                                                            const separator = `\n\n──────────────────────\n📝 CÂU HỎI TRUY VẤN:\n\n${questionsList}\n\n──────────────────────\n💬 PHẦN TRẢ LỜI CỦA BẠN:\n\n`;

                                                            setTextInput(prev => prev + separator);
                                                            setReviewResult(null);

                                                            // Focus textarea after state updates
                                                            setTimeout(() => {
                                                                textareaRef.current?.focus();
                                                                // Scroll to bottom
                                                                if (textareaRef.current) {
                                                                    textareaRef.current.selectionStart = textareaRef.current.value.length;
                                                                    textareaRef.current.selectionEnd = textareaRef.current.value.length;
                                                                }
                                                            }, 50);
                                                        }}
                                                        className="flex-1 py-2.5 md:py-3 px-3 md:px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm md:text-base font-bold rounded-lg shadow-md transition-all active:scale-95"
                                                    >
                                                        💬 Answer Follow-up
                                                    </button>
                                                )}

                                                {/* Start Fresh Button (Secondary) */}
                                                <button
                                                    onClick={() => {
                                                        setReviewResult(null);
                                                        setTextInput('');
                                                    }}
                                                    className={`py-2.5 md:py-3 px-3 md:px-4 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-400 text-sm md:text-base font-medium rounded-lg border border-gray-300 dark:border-slate-600 transition-colors ${reviewResult.followUpQuestions && reviewResult.followUpQuestions.length > 0 ? '' : 'flex-1'
                                                        }`}
                                                >
                                                    🔄 Start Fresh
                                                </button>
                                            </div>
                                        </div>
                                    )}



                                    {/* Footer Actions: Toggle + Mic + Retry + Submit */}
                                    {!reviewResult && (
                                        <div className="w-full flex items-center justify-between mt-3 md:mt-4 gap-2 md:gap-3">

                                            {/* Mode Toggle (Desktop Only) */}
                                            {!isMobile && (
                                                <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 rounded-full p-0.5 shadow-sm">
                                                    <button
                                                        onClick={() => setUseRealTimeDictation(true)}
                                                        className={`px-2 md:px-3 py-1 md:py-1.5 rounded-full text-[10px] md:text-xs font-bold transition-all ${useRealTimeDictation
                                                            ? 'bg-blue-500 text-white shadow-sm'
                                                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                                                            }`}
                                                    >
                                                        Real-time
                                                    </button>
                                                    <button
                                                        onClick={() => setUseRealTimeDictation(false)}
                                                        className={`px-2 md:px-3 py-1 md:py-1.5 rounded-full text-[10px] md:text-xs font-bold transition-all ${!useRealTimeDictation
                                                            ? 'bg-gray-600 dark:bg-slate-700 text-white shadow-sm'
                                                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                                                            }`}
                                                    >
                                                        Gemini
                                                    </button>
                                                </div>
                                            )}

                                            <div className="flex items-center gap-2 md:gap-3">
                                                {/* Retry Button (appears only on transcription failure) */}
                                                {hasTranscriptionError && savedAudioBlobRef.current && !useRealTimeDictation && (
                                                    <div className="flex flex-col items-end gap-1 animate-in fade-in slide-in-from-right-4 duration-300">
                                                        <span className="text-[10px] font-bold text-orange-600 bg-orange-100 dark:bg-orange-900/30 px-2 py-0.5 rounded-full border border-orange-200 dark:border-orange-800 shadow-sm">
                                                            {language === 'vietnamese' ? 'Chưa gửi được' : 'Upload Failed'}
                                                        </span>
                                                        <button
                                                            onClick={handleRetryTranscription}
                                                            disabled={isTranscribing}
                                                            className="h-8 md:h-12 px-2 md:px-5 rounded-full shadow-lg flex items-center gap-1 md:gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold text-[10px] md:text-sm transition-all hover:scale-105 active:scale-95 disabled:opacity-70 disabled:grayscale disabled:cursor-not-allowed"
                                                            title={language === 'vietnamese' ? 'Bấm để gửi lại' : 'Click to retry'}
                                                        >
                                                            {isTranscribing ? (
                                                                <>
                                                                    <Loader2 size={14} className="md:hidden animate-spin" />
                                                                    <Loader2 size={16} className="hidden md:block animate-spin" />
                                                                    <span className="hidden md:inline">{language === 'vietnamese' ? 'Đang gửi...' : 'Sending...'}</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="md:hidden">
                                                                        <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                                                        <path d="M3 3v5h5" />
                                                                        <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                                                                        <path d="M16 16h5v5" />
                                                                    </svg>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="hidden md:block">
                                                                        <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                                                        <path d="M3 3v5h5" />
                                                                        <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                                                                        <path d="M16 16h5v5" />
                                                                    </svg>
                                                                    <span className="hidden md:inline">{language === 'vietnamese' ? 'Gửi lại' : 'Retry'}</span>
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Mic Button */}
                                                <button
                                                    onClick={handleVoiceRecording}
                                                    disabled={isTranscribing}
                                                    className={`p-2 md:p-3 rounded-full transition-all shadow-md flex items-center justify-center ${(isRecording || isListening)
                                                        ? 'bg-red-500 text-white animate-pulse'
                                                        : isTranscribing
                                                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                                            : 'bg-white dark:bg-slate-800 text-indigo-600 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/30'
                                                        }`}
                                                    title={useRealTimeDictation
                                                        ? (language === 'vietnamese' ? 'Chế độ Real-time' : 'Real-time Mode')
                                                        : (language === 'vietnamese' ? 'Chế độ Gemini AI' : 'Gemini AI Mode')}
                                                >
                                                    {isTranscribing ? <Loader2 size={18} className="md:hidden animate-spin" /> : <Mic size={18} className="md:hidden" />}
                                                    {isTranscribing ? <Loader2 size={24} className="hidden md:block animate-spin" /> : <Mic size={24} className="hidden md:block" />}
                                                </button>

                                                {/* Clean Up Button */}
                                                <button
                                                    onClick={handleRefineText}
                                                    disabled={isRefining || !textInput.trim() || !!reviewResult}
                                                    className={`p-2 md:p-3 rounded-full transition-all shadow-md flex items-center justify-center ${isRefining
                                                        ? 'bg-indigo-100 text-indigo-400 cursor-wait'
                                                        : 'bg-white dark:bg-slate-800 text-indigo-600 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/30'
                                                        }`}
                                                    title={language === 'vietnamese' ? 'Làm sạch văn bản (AI)' : 'Clean Up Text (AI)'}
                                                >
                                                    {isRefining ? <Loader2 size={18} className="md:hidden animate-spin" /> : <Sparkles size={18} className="md:hidden" />}
                                                    {isRefining ? <Loader2 size={24} className="hidden md:block animate-spin" /> : <Sparkles size={24} className="hidden md:block" />}
                                                </button>

                                                {/* Submit Button */}
                                                <button
                                                    onClick={handleSubmit}
                                                    className="px-3 md:px-6 py-2 md:py-3 bg-green-500 hover:bg-green-600 text-white text-xs md:text-base font-bold rounded-full shadow-lg shadow-green-500/30 flex items-center gap-1 md:gap-2 transition-transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    disabled={isProcessing || !textInput.trim()}
                                                >
                                                    {isProcessing ? <Loader2 size={16} className="md:hidden animate-spin" /> : <CheckCircle2 size={16} className="md:hidden" />}
                                                    {isProcessing ? <Loader2 size={20} className="hidden md:block animate-spin" /> : <CheckCircle2 size={20} className="hidden md:block" />}
                                                    <span className="hidden sm:inline">Submit Review</span>
                                                    <span className="sm:hidden">Submit</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                /* History View */
                                <div className="absolute inset-0 overflow-y-auto p-4">
                                    {node.reviewSessions && node.reviewSessions.length > 0 ? (
                                        <div className="space-y-3">
                                            {node.reviewSessions.map((session, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`p-4 rounded-xl border-2 ${session.score >= 80 ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800' :
                                                        session.score >= 60 ? 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-800' :
                                                            'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
                                                        }`}
                                                >
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-3 mb-1">
                                                                <span className={`text-2xl font-black ${session.score >= 80 ? 'text-green-600' :
                                                                    session.score >= 60 ? 'text-yellow-600' :
                                                                        'text-red-600'
                                                                    }`}>{session.score}%</span>
                                                                <span className="text-xs text-gray-500">
                                                                    {new Date(session.timestamp).toLocaleString()}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{session.feedback}</p>

                                                            {/* Analysis Points in History */}
                                                            {session.analysisPoints && session.analysisPoints.length > 0 && (
                                                                <details className="text-xs mb-2">
                                                                    <summary className="cursor-pointer hover:text-indigo-600 font-medium text-gray-600 dark:text-gray-400">View Detailed Analysis</summary>
                                                                    <div className="mt-2 space-y-2">
                                                                        {session.analysisPoints.map((point, pIdx) => (
                                                                            <div key={pIdx} className={`p-2 rounded border text-xs ${point.verdict === 'correct' ? 'bg-green-50/30 border-green-200 dark:border-green-900/20' :
                                                                                point.verdict === 'incorrect' ? 'bg-red-50/30 border-red-200 dark:border-red-900/20' :
                                                                                    'bg-yellow-50/30 border-yellow-200 dark:border-yellow-900/20'
                                                                                }`}>
                                                                                <div className="flex gap-1.5">
                                                                                    <span className="shrink-0">
                                                                                        {point.verdict === 'correct' && '✅'}
                                                                                        {point.verdict === 'incorrect' && '❌'}
                                                                                        {point.verdict === 'partial' && '⚠️'}
                                                                                    </span>
                                                                                    <div>
                                                                                        <p className="italic text-gray-500 mb-0.5">"{point.quote}"</p>
                                                                                        <p className="text-gray-700 dark:text-gray-300">{point.explanation}</p>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </details>
                                                            )}

                                                            {/* Follow-up Questions in History */}
                                                            {session.followUpQuestions && session.followUpQuestions.length > 0 && (
                                                                <details className="text-xs mb-2">
                                                                    <summary className="cursor-pointer hover:text-indigo-600 font-medium text-gray-600 dark:text-gray-400">View Follow-up Questions</summary>
                                                                    <ul className="mt-2 list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400 bg-indigo-50/30 dark:bg-indigo-900/10 p-2 rounded border border-indigo-100 dark:border-indigo-900/30">
                                                                        {session.followUpQuestions.map((q, qIdx) => (
                                                                            <li key={qIdx}>{q}</li>
                                                                        ))}
                                                                    </ul>
                                                                </details>
                                                            )}

                                                            <details className="text-xs text-gray-600 dark:text-gray-400">
                                                                <summary className="cursor-pointer hover:text-indigo-600 font-medium">View Explanation</summary>
                                                                <p className="mt-2 p-2 bg-white dark:bg-slate-800 rounded border border-gray-200 dark:border-slate-700 whitespace-pre-wrap">
                                                                    {session.userExplanation}
                                                                </p>
                                                            </details>
                                                        </div>
                                                        <div className="flex flex-col gap-2 ml-4">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (confirm('Delete this review session?')) {
                                                                        onDeleteSession && onDeleteSession(session.timestamp); // Use timestamp as ID for now
                                                                    }
                                                                }}
                                                                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                                title="Delete Session"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 p-6 text-center">
                                            <div className="p-4 bg-gray-100 dark:bg-slate-800 rounded-full mb-4">
                                                <Trash2 size={24} className="opacity-20" />
                                            </div>
                                            <p className="font-medium mb-1">No review history yet</p>
                                            <p className="text-xs opacity-70">Complete your first review to see it here.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                {/* Image Gallery */}
                {imageGalleryOpen && allImages.length > 0 && (
                    <ImageGalleryModal
                        images={allImages}
                        initialIndex={initialGalleryIndex}
                        onClose={() => setImageGalleryOpen(false)}
                    />
                )}

                {/* Mobile Peek Overlay */}
                {isPeeking && (
                    <div className="md:hidden fixed inset-0 bg-black/80 z-[110] flex flex-col animate-in fade-in duration-200">
                        <div className="flex items-center justify-between p-4 bg-yellow-100 dark:bg-yellow-900/50 border-b border-yellow-300 dark:border-yellow-800">
                            <span className="text-sm font-bold text-yellow-800 dark:text-yellow-200 uppercase tracking-wide">📖 Original Note</span>
                            <button
                                onClick={() => setIsPeeking(false)}
                                className="p-2 hover:bg-yellow-200 dark:hover:bg-yellow-800 rounded-full transition-colors"
                            >
                                <X size={20} className="text-yellow-800 dark:text-yellow-200" />
                            </button>
                        </div>

                        {/* Images */}
                        {allImages.length > 0 && (
                            <div className="bg-white dark:bg-slate-900 p-4 border-b border-gray-200 dark:border-slate-700 flex gap-2 overflow-x-auto">
                                {allImages.map((img, idx) => (
                                    <img
                                        key={idx}
                                        src={img.src}
                                        alt={img.alt}
                                        className="max-h-[30vh] w-auto object-contain rounded-lg flex-shrink-0"
                                        onClick={() => { setInitialGalleryIndex(idx); setImageGalleryOpen(true); }}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Note Content */}
                        <div className="flex-1 overflow-y-auto p-4 bg-white dark:bg-slate-900 prose prose-sm dark:prose-invert max-w-none">
                            {node.content || "(No content)"}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
